import api from '../config/api';
import { categoriesToEventTypes } from '../utils/eventCategories';
import { requestDateFrom } from '../utils/filters';

// Searches used to be logged with a persistent random ID kept in the browser.
// Analytics no longer carries any identifier (so no consent banner is needed);
// clear the one earlier visits left behind.
try {
  localStorage.removeItem('userId');
} catch {
  // Storage can be blocked (private mode); then there is nothing to clear.
}

const appendList = (params, key, value) => {
  if (!value) {
    return;
  }
  const list = Array.isArray(value) ? value : [value];
  list.filter((item) => item !== undefined && item !== '').forEach((item) => params.append(key, item));
};

export function buildSearchParams(filters = {}) {
  const params = new URLSearchParams();

  if (filters.location) params.append('location', filters.location);
  if (filters.city) params.append('city', filters.city);
  if (filters.country) params.append('country', filters.country);
  if (filters.venue) params.append('venue', filters.venue);

  // The UI works in consolidated categories; the API still filters raw types.
  appendList(params, 'event_type', categoriesToEventTypes(filters.categories));
  appendList(params, 'music_group', filters.musicGroups);
  appendList(params, 'music_genre', filters.music_genre);
  appendList(params, 'special_features', filters.special_features);
  appendList(params, 'district', filters.districts);

  if (filters.dateFrom) params.append('dateFrom', requestDateFrom(filters));
  if (filters.dateTo) params.append('dateTo', filters.dateTo);
  if (filters.freeOnly) params.append('freeOnly', 'true');
  if (filters.search) params.append('search', filters.search);
  if (filters.page) params.append('page', filters.page);
  if (filters.limit) params.append('limit', filters.limit);
  if (filters.sortBy) params.append('sortBy', filters.sortBy);
  if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);
  if (filters.userLat != null) params.append('userLat', filters.userLat);
  if (filters.userLng != null) params.append('userLng', filters.userLng);

  return params;
}

export const searchEvents = async (filters = {}) => {
  const response = await api.get(`/events?${buildSearchParams(filters).toString()}`);

  // Fire-and-forget: analytics must never delay or break a search.
  logSearchAnalytics(filters, response.data?.pagination?.total || 0);

  return response.data;
};

// How many events a filter draft would return. Asks for a single row and reads
// the total, and deliberately skips analytics - this fires while the user is
// still choosing.
export const countEvents = async (filters = {}) => {
  const params = buildSearchParams({ ...filters, limit: 1, page: 1 });
  const response = await api.get(`/events?${params.toString()}`);
  return response.data?.pagination?.total ?? 0;
};

export const getEventById = async (id, { userLat, userLng } = {}) => {
  const params = new URLSearchParams();
  if (userLat != null) params.append('userLat', userLat);
  if (userLng != null) params.append('userLng', userLng);

  const query = params.toString();
  const response = await api.get(`/events/${id}${query ? `?${query}` : ''}`);
  return response.data;
};

// Used by the favourites list to refresh every saved event in one request.
export const getEventsByIds = async (ids = []) => {
  if (ids.length === 0) {
    return [];
  }
  const params = new URLSearchParams();
  ids.forEach((id) => params.append('ids', id));
  const response = await api.get(`/events/by-ids?${params.toString()}`);
  return response.data.events || [];
};

// The event highlighted in the admin panel, or null. Decoration only: any
// failure just means no highlight.
export const getFeaturedEvent = async () => {
  try {
    const response = await api.get('/events/featured');
    return response.data?.event || null;
  } catch (error) {
    console.warn('Could not load the featured event:', error.message);
    return null;
  }
};

export const getEventOccurrences = async (id) => {
  try {
    const response = await api.get(`/events/${id}/occurrences`);
    return response.data.occurrences || [];
  } catch (error) {
    console.warn('Could not load other dates:', error.message);
    return [];
  }
};

const EMPTY_OPTIONS = {
  musicGenres: [],
  musicGroups: [],
  eventTypes: [],
  cities: [],
  countries: [],
  venues: [],
  specialFeatures: [],
  districts: [],
  priceRange: { min: 0, max: 0 },
  totals: { total: 0, free: 0 }
};

export const getFilterOptions = async () => {
  try {
    const response = await api.get('/events/filters/options');
    return { ...EMPTY_OPTIONS, ...response.data };
  } catch (error) {
    console.error('Error fetching filter options:', error);
    return EMPTY_OPTIONS;
  }
};

const logSearchAnalytics = async (searchCriteria, resultsCount) => {
  try {
    const searchPayload = {
      freeOnly: Boolean(searchCriteria.freeOnly)
    };

    if (searchCriteria.city || searchCriteria.country) {
      searchPayload.location = {
        city: searchCriteria.city,
        country: searchCriteria.country
      };
    }
    if (searchCriteria.dateFrom || searchCriteria.dateTo) {
      searchPayload.dateRange = {
        start: searchCriteria.dateFrom ? new Date(searchCriteria.dateFrom) : undefined,
        end: searchCriteria.dateTo ? new Date(searchCriteria.dateTo) : undefined
      };
    }
    if (searchCriteria.search) {
      searchPayload.searchQuery = searchCriteria.search;
    }
    if (searchCriteria.musicGroups?.length) {
      searchPayload.musicGroups = searchCriteria.musicGroups;
    }
    if (searchCriteria.music_genre?.length) {
      searchPayload.musicGenre = searchCriteria.music_genre;
    }
    if (searchCriteria.categories?.length) {
      searchPayload.eventType = categoriesToEventTypes(searchCriteria.categories);
    }
    if (searchCriteria.special_features?.length) {
      searchPayload.specialFeatures = searchCriteria.special_features;
    }
    if (searchCriteria.districts?.length) {
      searchPayload.districts = searchCriteria.districts;
    }

    await api.post('/analytics', {
      searchCriteria: searchPayload,
      resultsCount,
      platform: 'web'
    });
  } catch (error) {
    console.error('Error logging analytics:', error);
  }
};
